import { Geolocation } from '@capacitor/geolocation';
import type { LatLng } from '@tcrd/shared';
import { env } from './env';

export class LocationError extends Error {
  constructor(
    public readonly code: 'DENIED' | 'UNAVAILABLE' | 'TIMEOUT',
    message: string,
  ) {
    super(message);
  }
}

/** Ubicación puntual del dispositivo (para "usar mi ubicación"). */
export async function currentPosition(): Promise<LatLng & { accuracy: number }> {
  try {
    if (env.isNative) {
      const perm = await Geolocation.checkPermissions();
      if (perm.location !== 'granted') {
        const req = await Geolocation.requestPermissions({ permissions: ['location'] });
        if (req.location !== 'granted') throw new LocationError('DENIED', 'Permiso de ubicación denegado. Actívalo en Ajustes > Aplicaciones > TuConductorRD.');
      }
    }
    if (!env.isNative) return await browserPosition();
    const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 });
    return { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
  } catch (e) {
    if (e instanceof LocationError) throw e;
    const msg = String((e as Error).message ?? e).toLowerCase();
    if (msg.includes('denied') || msg.includes('permission')) throw new LocationError('DENIED', 'Permiso de ubicación denegado en el navegador.');
    if (msg.includes('timeout')) throw new LocationError('TIMEOUT', 'No se pudo obtener tu ubicación a tiempo. Intenta de nuevo.');
    throw new LocationError('UNAVAILABLE', 'Ubicación no disponible. Activa el GPS.');
  }
}

const getPos = (opts: PositionOptions) =>
  new Promise<GeolocationPosition>((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, opts));

/** Navegador (incluido Safari en iPhone): si la alta precisión falla, reintenta con la ubicación aproximada. */
async function browserPosition(): Promise<LatLng & { accuracy: number }> {
  if (!('geolocation' in navigator)) throw new LocationError('UNAVAILABLE', 'Este navegador no permite obtener la ubicación.');
  let pos: GeolocationPosition;
  try {
    pos = await getPos({ enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 });
  } catch (e) {
    const err = e as GeolocationPositionError;
    if (err.code === 1) {
      throw new LocationError(
        'DENIED',
        /iPhone|iPad/.test(navigator.userAgent)
          ? 'Permiso de ubicación denegado. En el iPhone: Ajustes > Privacidad > Localización > Sitios web de Safari > "Al usar la app".'
          : 'Permiso de ubicación denegado en el navegador.',
      );
    }
    try {
      pos = await getPos({ enableHighAccuracy: false, timeout: 15_000, maximumAge: 300_000 });
    } catch (e2) {
      const c = (e2 as GeolocationPositionError).code;
      if (c === 3) throw new LocationError('TIMEOUT', 'No se pudo obtener tu ubicación a tiempo. Intenta de nuevo.');
      throw new LocationError('UNAVAILABLE', 'Ubicación no disponible. Activa la localización del teléfono.');
    }
  }
  return { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
}

/** Permiso ya concedido (sin mostrar el diálogo). En navegadores sin la API de permisos devuelve null. */
export async function locationAlreadyGranted(): Promise<boolean | null> {
  try {
    if (env.isNative) return (await Geolocation.checkPermissions()).location === 'granted';
    if (!navigator.permissions) return null;
    return (await navigator.permissions.query({ name: 'geolocation' })).state === 'granted';
  } catch {
    return null;
  }
}
